import React, { useMemo, useState, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(BigCalendar);

const VIEW_OPTIONS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' }
];

const parseDateTime = (dt) => {
  if (!dt) return null;
  // Format expected: YYYY-MM-DDTHH:mm  (or YYYY-MM-DD)
  const m = moment(dt, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.toDate() : null;
};

const formatDateTime = (date) => moment(date).format('YYYY-MM-DDTHH:mm');

const buildEvents = (schedules) => {
  const events = [];
  (schedules || []).forEach(schedule => {
    (schedule.meetings || []).forEach(meeting => {
      const start = parseDateTime(meeting.date_time);
      if (!start) return;
      // Use date_time_to if present and valid; else default to +1h
      const explicitEnd = parseDateTime(meeting.date_time_to);
      const end = (explicitEnd && explicitEnd.getTime() > start.getTime())
        ? explicitEnd
        : moment(start).add(1, 'hour').toDate();
      events.push({
        id: `${schedule.id}__${meeting.id}`,
        scheduleId: schedule.id,
        meetingId: meeting.id,
        title: meeting.meeting_name || 'Meeting',
        location: meeting.location,
        attendees: meeting.attendees,
        purpose: meeting.purpose,
        category: meeting.category || '',
        start,
        end
      });
    });
  });
  return events;
};

const CATEGORY_HEX = {
  'command-staff': { bg: '#b91c1c', border: '#7f1d1d' },
  'operations': { bg: '#f97316', border: '#c2410c' },
  'planning': { bg: '#3b82f6', border: '#1d4ed8' },
  'logistics': { bg: '#f59e0b', border: '#b45309' },
  'fin-admin': { bg: '#6b7280', border: '#374151' }
};

const getEventColors = (category) => {
  return CATEGORY_HEX[category] || { bg: '#ea580c', border: '#c2410c' };
};

// Year view: 12 mini-month grid showing event dots per day.
const YearView = ({ date, events, onDrillMonth }) => {
  const year = moment(date).year();
  const months = Array.from({ length: 12 }, (_, i) => i);

  const eventCountsByMonth = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const key = `${moment(e.start).year()}-${moment(e.start).month()}-${moment(e.start).date()}`;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [events]);

  const renderMiniMonth = (monthIdx) => {
    const monthStart = moment({ year, month: monthIdx, day: 1 });
    const daysInMonth = monthStart.daysInMonth();
    const firstWeekday = monthStart.day(); // 0 (Sun) - 6 (Sat)
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div
        key={monthIdx}
        className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 cursor-pointer hover:border-orange-500/60 transition-colors"
        onClick={() => onDrillMonth(monthStart.toDate())}
        data-testid={`year-mini-month-${monthIdx}`}
      >
        <div className="text-orange-400 text-sm font-semibold mb-2 text-center">
          {monthStart.format('MMMM')}
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-[10px] text-gray-500 mb-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((d, i) => {
            const key = d ? `${year}-${monthIdx}-${d}` : null;
            const count = key ? eventCountsByMonth[key] || 0 : 0;
            const isToday = d && moment().isSame(moment({ year, month: monthIdx, day: d }), 'day');
            return (
              <div
                key={i}
                className={`relative aspect-square flex items-center justify-center text-[10px] rounded ${
                  d ? (isToday ? 'bg-orange-500/30 text-white font-bold' : 'text-gray-300 hover:bg-gray-800') : ''
                }`}
              >
                {d || ''}
                {count > 0 && (
                  <span className="absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-orange-500" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-2">
      {months.map(renderMiniMonth)}
    </div>
  );
};

const MeetingCalendarView = ({
  schedules,
  onSelectSlot,
  onSelectEvent,
  onEventDrop
}) => {
  const [calendarView, setCalendarView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());

  const events = useMemo(() => buildEvents(schedules), [schedules]);

  const handleNavigate = useCallback((newDate) => setDate(newDate), []);

  const navYear = (delta) => {
    setDate(moment(date).add(delta, 'year').toDate());
  };

  const handleEventDrop = useCallback(({ event, start }) => {
    if (onEventDrop) onEventDrop(event, formatDateTime(start));
  }, [onEventDrop]);

  const handleSelectSlot = useCallback(({ start }) => {
    if (onSelectSlot) onSelectSlot(formatDateTime(start));
  }, [onSelectSlot]);

  const handleDrillMonth = (monthDate) => {
    setDate(monthDate);
    setCalendarView(Views.MONTH);
  };

  const titleLabel = calendarView === 'year'
    ? moment(date).format('YYYY')
    : moment(date).format(
        calendarView === Views.MONTH ? 'MMMM YYYY'
          : calendarView === Views.WEEK ? '[Week of] MMM D, YYYY'
          : 'dddd, MMMM D, YYYY'
      );

  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4" data-testid="ics230-calendar-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            onClick={() => {
              if (calendarView === 'year') navYear(-1);
              else setDate(moment(date).subtract(1, calendarView).toDate());
            }}
            data-testid="cal-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            onClick={() => setDate(new Date())}
            data-testid="cal-today-btn"
          >
            Today
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            onClick={() => {
              if (calendarView === 'year') navYear(1);
              else setDate(moment(date).add(1, calendarView).toDate());
            }}
            data-testid="cal-next-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-3 text-white font-semibold text-base" data-testid="cal-title">
            {titleLabel}
          </span>
        </div>
        <div className="flex items-center gap-1 bg-gray-800/60 p-1 rounded-md">
          {VIEW_OPTIONS.map(v => (
            <button
              key={v.key}
              type="button"
              onClick={() => setCalendarView(v.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded transition-colors ${
                calendarView === v.key
                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
              data-testid={`cal-view-${v.key}-btn`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar body */}
      {calendarView === 'year' ? (
        <YearView date={date} events={events} onDrillMonth={handleDrillMonth} />
      ) : (
        <div className="rbc-dark-wrapper" style={{ height: 600 }}>
          <DnDCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={calendarView}
            onView={setCalendarView}
            date={date}
            onNavigate={handleNavigate}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            toolbar={false}
            selectable
            resizable={false}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={(e) => onSelectEvent && onSelectEvent(e)}
            onEventDrop={handleEventDrop}
            popup
            eventPropGetter={(event) => {
              const colors = getEventColors(event.category);
              return {
                style: {
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '12px'
                }
              };
            }}
          />
        </div>
      )}

      {/* Dark theme overrides for react-big-calendar */}
      <style>{`
        .rbc-dark-wrapper .rbc-calendar { background: transparent; color: #e5e7eb; }
        .rbc-dark-wrapper .rbc-month-view,
        .rbc-dark-wrapper .rbc-time-view,
        .rbc-dark-wrapper .rbc-agenda-view { border-color: #374151; }
        .rbc-dark-wrapper .rbc-header { background: #1f2937; color: #e5e7eb; border-color: #374151; padding: 8px; font-weight: 600; }
        .rbc-dark-wrapper .rbc-day-bg { background: #111827; border-color: #374151; }
        .rbc-dark-wrapper .rbc-off-range-bg { background: #0b1220; }
        .rbc-dark-wrapper .rbc-today { background: rgba(234,88,12,0.12); }
        .rbc-dark-wrapper .rbc-date-cell { color: #9ca3af; padding: 4px; }
        .rbc-dark-wrapper .rbc-date-cell.rbc-now > a { color: #fb923c; font-weight: 700; }
        .rbc-dark-wrapper .rbc-month-row,
        .rbc-dark-wrapper .rbc-month-row + .rbc-month-row { border-color: #374151; }
        .rbc-dark-wrapper .rbc-time-content { border-color: #374151; }
        .rbc-dark-wrapper .rbc-time-header-content,
        .rbc-dark-wrapper .rbc-time-header.rbc-overflowing { border-color: #374151; }
        .rbc-dark-wrapper .rbc-time-slot { border-color: #1f2937; color: #9ca3af; }
        .rbc-dark-wrapper .rbc-timeslot-group { border-color: #374151; }
        .rbc-dark-wrapper .rbc-current-time-indicator { background: #fb923c; }
        .rbc-dark-wrapper .rbc-show-more { color: #fb923c; background: transparent; }
        .rbc-dark-wrapper .rbc-event { box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
        .rbc-dark-wrapper .rbc-event.rbc-selected { background-color: #f97316 !important; }
        .rbc-dark-wrapper .rbc-day-slot .rbc-events-container { margin-right: 2px; }
        .rbc-dark-wrapper .rbc-row-segment .rbc-event-content { font-size: 11px; }
        .rbc-dark-wrapper .rbc-agenda-table.table { color: #e5e7eb; }
      `}</style>
    </div>
  );
};

export default MeetingCalendarView;
